import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, X as XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Label } from '../ui/Label';
import { Switch } from '../ui/Switch';
import { Separator } from '../ui/Separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { useAnonymousReviewContext } from './AnonymousReviewProvider';

const AnonymousStep2: React.FC = () => {
  const navigate = useNavigate();
  const { data, updateData, error, validateStep2 } = useAnonymousReviewContext();
  
  // On mount, read URL params for preuploaded storage path from landing page
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromLanding = params.get('fromLandingPage') === 'true';
    const storagePath = params.get('storagePath') || undefined;
    const fileName = params.get('fileName') || undefined;
    const title = params.get('title') || undefined;
    if (fromLanding && storagePath) {
      updateData({
        preuploadedStoragePath: storagePath,
        preuploadedFileName: fileName,
        hasFilesFromLandingPage: true,
        title: data.title || (title ? decodeURIComponent(title) : data.title)
      } as any);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Helper functions (same as original)
  const getCurrentDate = () => new Date().toISOString().split('T')[0];
  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  };

  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleContinue = () => {
    if (validateStep2()) {
      navigate('../step-3');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Try Approvia - Review Details
        </h1>
        <p className="text-muted-foreground">
          Step 2 of 3: Configure your review settings
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Progress</span>
          <span className="text-sm text-muted-foreground">2 of 3</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div className="bg-primary h-2 rounded-full" style={{ width: '67%' }}></div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Setup Review Request</CardTitle>
          <CardDescription>Provide details for your review request</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
           {/* Show current title and file info (read-only) */}
          {data.title && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <Label className="text-sm font-medium">Review Title:</Label>
              <p className="text-sm mt-1 font-medium">{data.title}</p>
               {data.preuploadedStoragePath && (
                 <div className="mt-2">
                   <Label className="text-sm font-medium">Uploaded File:</Label>
                   <p className="text-sm mt-1 text-green-600">
                     ✓ {data.preuploadedFileName || 'File'} (from landing page)
                   </p>
                   <p className="text-xs text-muted-foreground mt-1">
                     File already uploaded. No need to re-upload.
                   </p>
                 </div>
               )}
              {data.hasFilesFromLandingPage && data.files.length > 0 && (
                <div className="mt-2">
                  <Label className="text-sm font-medium">Uploaded File:</Label>
                  <p className="text-sm mt-1 text-green-600">
                    ✓ {data.files[0].name} ({(data.files[0].size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    File already uploaded - ready for creatingreview!
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                To change the title or file, go back to Step 1.
              </p>
            </div>
          )}



          {/* Creator Email - required */}
          <div>
            <Label htmlFor="creator-email">Your Email<span className="text-red-500">*</span></Label>
            <Input
              id="creator-email"
              type="email"
              placeholder="your.email@example.com"
              value={data.creatorEmail}
              onChange={(e) => updateData({ creatorEmail: e.target.value })}
              required
            />
          </div>

          {/* Reviewer Email - required */}
          <div>
            <Label htmlFor="reviewer-email">Reviewer Email<span className="text-red-500">*</span></Label>
            <Input
              id="reviewer-email"
              type="email"
              placeholder="reviewer@company.com"
              value={data.reviewerEmail}
              onChange={(e) => updateData({ reviewerEmail: e.target.value })}
              required
            />
          </div>

          {/* Optional reviewer names */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="reviewer-first-name">Reviewer First Name (optional)</Label>
              <Input
                id="reviewer-first-name"
                type="text"
                placeholder="First Name"
                value={data.reviewerFirstName || ''}
                onChange={(e) => updateData({ reviewerFirstName: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="reviewer-last-name">Reviewer Last Name (optional)</Label>
              <Input
                id="reviewer-last-name"
                type="text"
                placeholder="Last Name"
                value={data.reviewerLastName || ''}
                onChange={(e) => updateData({ reviewerLastName: e.target.value })}
              />
            </div>
          </div>

          {/* Divider before Deadline section */}
          <Separator className="my-4" />

          {/* Deadline Toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="deadline-toggle"
              checked={data.deadlineEnabled || false}
              onCheckedChange={(checked) => updateData({ deadlineEnabled: checked })}
            />
            <Label htmlFor="deadline-toggle">Set Deadline</Label>
          </div>

          {data.deadlineEnabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="deadline-date" className="text-sm">
                    Deadline Date
                    <span className="text-xs text-muted-foreground ml-1 block md:inline">
                      ({localTimeZone})
                    </span>
                  </Label>
                  <Input
                    id="deadline-date"
                    type="date"
                    min={getCurrentDate()}
                    value={data.deadline}
                    onChange={(e) => updateData({ deadline: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="deadline-time" className="text-sm">
                    Deadline Time
                    <span className="text-xs text-muted-foreground ml-1 block md:inline">
                      ({localTimeZone})
                    </span>
                  </Label>
                  <Input
                    id="deadline-time"
                    type="time"
                    min={data.deadline === getCurrentDate() ? getCurrentTime() : undefined}
                    value={data.deadlineTime || ''}
                    onChange={(e) => updateData({ deadlineTime: e.target.value })}
                  />
                </div>
              </div>

              {/* Deadline validation message */}
              {(!!data.deadline !== !!data.deadlineTime) && (
                <div className="text-red-500 text-xs mt-1">
                  Please enter both deadline date and time, or leave both empty.
                </div>
              )}
            </>
          )}

          {/* Reminder section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="reminder-toggle"
                checked={data.reminderEnabled}
                onCheckedChange={(checked) => updateData({ reminderEnabled: checked })}
                disabled={!data.deadlineEnabled || !data.deadline || !data.deadlineTime}
              />
              <Label htmlFor="reminder-toggle" className={!data.deadlineEnabled || !data.deadline || !data.deadlineTime ? 'text-muted-foreground' : ''}>
                Set Reminder
              </Label>
              {(!data.deadlineEnabled || !data.deadline || !data.deadlineTime) && (
                <span className="text-xs text-muted-foreground ml-2">
                  (Set deadline first)
                </span>
              )}
            </div>

            {data.reminderEnabled && data.deadlineEnabled && data.deadline && data.deadlineTime && (
              <div className="space-y-4 pl-6">
                {data.reminders.map((rem, idx) => (
                  <div key={idx} className="border rounded-lg p-4 space-y-4 relative">
                    {/* remove button */}
                    <button
                      type="button"
                      onClick={() => updateData({ reminders: data.reminders.filter((_, i) => i !== idx) })}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                    <Label>Reminder Options</Label>
                    <select
                      value={rem.type}
                      onChange={(e) => {
                        const newR = [...data.reminders];
                        newR[idx].type = e.target.value;
                        updateData({ reminders: newR });
                      }}
                      className="w-full border border-input rounded-md p-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="" disabled>Choose reminder time</option>
                      <option value="3-days">3 days before deadline</option>
                      <option value="1-day">1 day before deadline</option>
                      <option value="3-hours">3 hours before deadline</option>
                      <option value="custom">Custom date & time</option>
                    </select>

                    {rem.type && rem.type !== 'custom' && data.deadlineEnabled && data.deadline && data.deadlineTime && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <Label className="text-sm font-medium">Reminder will be sent at:</Label>
                        <p className="text-sm mt-1">
                          {(() => {
                            const deadlineDateTime = new Date(`${data.deadline}T${data.deadlineTime}`);
                            let reminderDateTime = new Date(deadlineDateTime);
                            switch (rem.type) {
                              case '3-days':
                                reminderDateTime.setDate(deadlineDateTime.getDate() - 3);
                                break;
                              case '1-day':
                                reminderDateTime.setDate(deadlineDateTime.getDate() - 1);
                                break;
                              case '3-hours':
                                reminderDateTime.setHours(deadlineDateTime.getHours() - 3);
                                break;
                            }
                            return reminderDateTime.toLocaleString('en-US', {
                              weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                            });
                          })()}
                        </p>
                      </div>
                    )}

                    {rem.type === 'custom' && (
                      <> 
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`reminder-date-${idx}`} className="text-sm">Reminder Date</Label>
                            <Input 
                              id={`reminder-date-${idx}`} 
                              type="date" 
                              min={getCurrentDate()}
                              value={rem.customDate || ''} 
                              onChange={(e) => {
                                const newR = [...data.reminders];
                                newR[idx].customDate = e.target.value;
                                updateData({ reminders: newR });
                              }} 
                            />
                          </div>
                          <div>
                            <Label htmlFor={`reminder-time-${idx}`} className="text-sm">Reminder Time</Label>
                            <Input 
                              id={`reminder-time-${idx}`} 
                              type="time" 
                              min={rem.customDate === getCurrentDate() ? getCurrentTime() : undefined}
                              value={rem.customTime || ''} 
                              onChange={(e) => {
                                const newR = [...data.reminders];
                                newR[idx].customTime = e.target.value;
                                updateData({ reminders: newR });
                              }} 
                            />
                          </div>
                        </div>
                        {rem.customDate && rem.customTime && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <Label className="text-sm font-medium">Reminder will be sent at:</Label>
                            <p className="text-sm mt-1">{new Date(`${rem.customDate}T${rem.customTime}`).toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {data.reminders.length < 3 && (
                  <Button type="button" variant="outline" size="sm" className="mt-2 gap-1" onClick={() => updateData({ reminders: [...data.reminders, { type: '' }] })}>
                    <Plus className="w-4 h-4" /> Add Reminder
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Comment section */}
          <div>
            <Label htmlFor="comment">Comment (Optional)</Label>
            <Textarea
              id="comment"
              placeholder="Add any specific instructions or context for the reviewer..."
              value={data.customInstructions}
              onChange={(e) => updateData({ customInstructions: e.target.value })}
              rows={4}
            />
          </div>


        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 mt-6">
          <CardContent className="pt-6">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => navigate('../step-1')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Step 1
        </Button>
        <Button onClick={handleContinue}>
          Continue to Step 3
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default AnonymousStep2; 